import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useEditPathStore } from './editPath'
import { useTasksStore } from './tasks'
import { usePlanner } from './planner'
import { DEFAULT_TRACK_WIDTH } from './drawing'
import {
    postEditPathSegmentPlanning,
    postEditedPathPlanning,
    getDefaultEditValues,
    postUploadedPathPlanning
} from '@/scripts/fieldsPlanning'
import { getClosestPointPolyline } from '@/helpers/geometryHelper'
import moment from 'moment'
import { downloadFeatureAsShapefile } from '@/scripts/shapefileExport'
import { postExtractGeometryFromShape } from '@/scripts/fieldsPlanning'
import booleanContains from '@turf/boolean-contains'
import { feature } from '@turf/helpers'
import i18n from '@/i18n'

export const useEasyModeStore = defineStore('easyMode', {
    state: () => ({
        easyMode: true,
        deleteButton: null,
        insertButton: null,
        switchButton: null,
        updatedChanges: false,
        redPinInterval: null,
        yellowWarningInterval: null,
        redPinIndex: null,
        fullPathGenerated: false,
        isFreeDrawingMode: false, // Flag to indicate if free drawing mode is active
        freeDrawingBooleans: [], // Array to hold booleans for free drawing points
        pathIndicesForCPs: [], // Array to hold indices of checkpoints in the segment path
        highlightedPolylines: [], // Array of highlighted path segments
        orangeVertices: [], // Array to hold orange vertex markers
        cpDragListeners: [],
        contextMenuListeners: [],
        easyModeMapClickListener: null,
        // Corridor and direction change variables for hook curves
        corridorThreshold: 0.2,
        hookCurveExtension: 9,
        toastText: '',
        warningIndices: [],
        isUpload: false,
        hasUploadedSegment: false,
        uploadedSegment: {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: null,
                        coordinates: null
                    },
                }
            ]
        },
        downloadablePolys: [],
        _wasListeningBeforeMeasure: null,
    }),
    actions: {
        addContextMenuToMarker(marker) {
            if (!marker) return

            const rawMarker = toRaw(marker)
            const ms = useMaps()
            if (marker.hasContextMenu) {
                return
            }
            marker.hasContextMenu = true

            // Handle click event
            ms.google.maps.event.addListener(rawMarker, 'click', () => {
                const eps = useEditPathStore()
                const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
                const currentIndex = checkpointMarkers.findIndex(m => m === marker)

                if (currentIndex >= 0) {
                    this.highlightPathSegment(currentIndex)
                }
            })

            // Add right-click handler for context menu
            rawMarker.addEventListener('contextmenu', (event) => {
                event.preventDefault()

                const eps = useEditPathStore()
                const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
                const currentIndex = checkpointMarkers.findIndex(m => m === marker)
                if (currentIndex >= 0) {
                    this.showDeleteButton(event, currentIndex)
                    this.showInsertButton(event, currentIndex)
                    this.showSwitchButton(event, currentIndex)
                }
            })
        },
        addDragendListenerToMarker(marker) {
            const ms = useMaps()
            const eps = useEditPathStore()

            if (marker.title !== 'Start Point' && marker.title !== 'End Point') { 
                const listener = ms.google.maps.event.addListener(
                    marker,
                    'dragend',
                    async () => {
                        const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
                        const idx = checkpointMarkers.findIndex(m => m === marker)

                        if (idx < 0) return

                        if (!this.freeDrawingBooleans[idx] || eps.markersOnPath) {
                            const trackSystem = ms.draw.field.abLines.concat(ms.draw.field.headlanes)
                            const pos = { lat: checkpointMarkers[idx].position.lat, lng: checkpointMarkers[idx].position.lng }
                            const snappedPosition = this.getClosestPointTurfEasy(pos, trackSystem, idx)

                            if (snappedPosition) {
                                checkpointMarkers[idx].position = snappedPosition
                                eps.checkpoints[idx].features[0].geometry.coordinates = [snappedPosition.lng, snappedPosition.lat]
                            }
                        }
                        await this.generateSegment(false, idx)
                    }
                )

                // keep cpDragListeners aligned with current checkpointMarkers at registration time
                const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
                const currentIndex = checkpointMarkers.findIndex(m => m === marker)
                if (currentIndex >= 0) {
                    this.cpDragListeners.splice(currentIndex, 0, listener)
                }
            } else {
                const listener = ms.google.maps.event.addListener(
                    marker,
                    'dragend',
                    async () => {
                        const pos = { lat: marker.position.lat, lng: marker.position.lng }
                        const snapTo = ms.draw.field.abLines
                        const snappedPosition = this.getClosestPointTurfEasy(pos, snapTo)
                        if (snappedPosition) {
                            if (marker?.title === 'Start Point') {
                                eps.startPoint = { lat: snappedPosition.lat, lng: snappedPosition.lng }
                            } else if (marker?.title === 'End Point') {
                                eps.endPoint = { lat: snappedPosition.lat, lng: snappedPosition.lng }
                            }
                            marker.position = snappedPosition
                        }                    
                        if (eps.markers.length >= 2) {
                            eps.updateOrangePath()
                        }
                        if (eps.startPoint && eps.endPoint) {
                            await this.generateSegment(false, -1)
                        }
                    }
                )
                this.cpDragListeners.push(listener)
            }
        },
        removeDragListener(listener) {
            const ms = useMaps()
            if (listener && typeof listener.remove === 'function') {
                listener.remove()
            } else {
                ms.google.maps.event.removeListener(listener)
            }
        },
        showDeleteButton(event, checkpointIndex) {
            // Close previous button if exists
            this.hideDeleteButton()

            // Create new button
            this.deleteButton = document.createElement('div')
            this.deleteButton.innerHTML = i18n.global.t('Delete')
            Object.assign(this.deleteButton.style, {
                position: 'absolute',
                backgroundColor: '#fff',
                color: '#dc3545',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: '10000',
                left: event.clientX + 'px',
                top: event.clientY + 'px'
            })

            // Hover effects
            this.deleteButton.onmouseover = () => {
                this.deleteButton.style.backgroundColor = '#dc3545'
                this.deleteButton.style.color = '#fff'
            }
            this.deleteButton.onmouseout = () => {
                this.deleteButton.style.backgroundColor = '#fff'
                this.deleteButton.style.color = '#dc3545'
            }

            // Click for deletion
            this.deleteButton.onclick = () => {
                this.deleteCheckpoint(checkpointIndex)
                this.hideDeleteButton()
            }

            // Add to document
            document.body.appendChild(this.deleteButton)

            // Close on click anywhere else
            setTimeout(() => {
                document.addEventListener('click', this.hideDeleteButton, { once: true })
            }, 100)
        },
        hideDeleteButton() {
            if (this.deleteButton) {
                this.deleteButton.remove()
                this.deleteButton = null
            }
        },
        showInsertButton(event, checkpointIndex) {
            // Close previous button if exists
            this.hideInsertButton()

            // Create new button
            this.insertButton = document.createElement('div')
            this.insertButton.innerHTML = i18n.global.t('Insert')
            const pixeloffset = checkpointIndex === -1 ? 0 : 40
            Object.assign(this.insertButton.style, {
                position: 'absolute',
                backgroundColor: '#fff',
                color: '#28a745',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: '10000',
                left: event.clientX + 'px',
                top: (event.clientY + pixeloffset) + 'px' // 40px below the delete button
            })

            // Hover effects
            this.insertButton.onmouseover = () => {
                this.insertButton.style.backgroundColor = '#28a745'
                this.insertButton.style.color = '#fff'
            }
            this.insertButton.onmouseout = () => {
                this.insertButton.style.backgroundColor = '#fff'
                this.insertButton.style.color = '#28a745'
            }

            // Click for insertion
            this.insertButton.onclick = () => {
                this.insertCheckpoint(checkpointIndex)
                this.hideInsertButton()
            }

            // Add to document
            document.body.appendChild(this.insertButton)

            // Close on click anywhere else
            setTimeout(() => {
                document.addEventListener('click', this.hideInsertButton, { once: true })
            }, 100)
        },
        hideInsertButton() {
            if (this.insertButton) {
                this.insertButton.remove()
                this.insertButton = null
            }
        },
        showSwitchButton(event, checkpointIndex) {
            // Close previous button if exists
            this.hideSwitchButton()

            // Create new button
            this.switchButton = document.createElement('div')
            this.switchButton.innerHTML = i18n.global.t('Toggle CP/FP')
            Object.assign(this.switchButton.style, {
                position: 'absolute',
                backgroundColor: '#fff',
                color: '#007bff',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                zIndex: '10000',
                left: event.clientX + 'px',
                top: (event.clientY + 80) + 'px' // 80px below the delete button
            })

            // Hover effects
            this.switchButton.onmouseover = () => {
                this.switchButton.style.backgroundColor = '#007bff'
                this.switchButton.style.color = '#fff'
            }
            this.switchButton.onmouseout = () => {
                this.switchButton.style.backgroundColor = '#fff'
                this.switchButton.style.color = '#007bff'
            }

            // Click for switching
            this.switchButton.onclick = () => {
                this.switchCheckpoint(checkpointIndex)
                this.hideSwitchButton()
            }

            // Add to document
            document.body.appendChild(this.switchButton)

            // Close on click anywhere else
            setTimeout(() => {
                document.addEventListener('click', this.hideSwitchButton, { once: true })
            }, 100)
        },
        hideSwitchButton() {
            if (this.switchButton) {
                this.switchButton.remove()
                this.switchButton = null
            }
        },
        async deleteCheckpoint(index) {
            const eps = useEditPathStore()
            const ms = useMaps()

            if (index < 0 || index >= eps.checkpoints.length) {
                return
            }

            // Remove from data array
            eps.checkpoints.splice(index, 1)
            this.freeDrawingBooleans.splice(index, 1)
            this.pathIndicesForCPs.splice(index + 1, 1)

            this.removeDragListener(this.cpDragListeners[index])
            this.cpDragListeners.splice(index, 1)

            // Find and remove visual marker
            const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
            if (checkpointMarkers[index]) {
                toRaw(checkpointMarkers[index]).setMap(null)
                eps.markers = eps.markers.filter(m => m !== checkpointMarkers[index])
            }

            // Renumber remaining checkpoints and markers
            eps.checkpoints.forEach((cp, i) => cp.features[0].id = i.toString())

            // Update remaining checkpoint markers - ONLY visual properties
            const remainingCheckpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
            remainingCheckpointMarkers.forEach((marker, i) => {
                const newNumber = i + 1
                let str = "CP"
                if (this.freeDrawingBooleans[i]) {
                    str = "FP"
                }
                const newText = `${str}${newNumber}`
                marker.title = `Checkpoint ${newNumber}`
                marker.markerId = `start_CP${newNumber}`

                // Update visual text by recreating PinElement
                const newPinElement = new ms.google.maps.marker.PinElement({
                    background: '#FFA500',
                    borderColor: '#000000',
                    glyphColor: '#FFFFFF',
                    scale: 1.4,
                    glyphText: String(newText)
                })
                marker.content = newPinElement
            })

            if (index > 0) {
                await this.generateSegment(false, index - 1)
            } else {
                await this.generateSegment(false)
            }
        },
        insertCheckpoint(index) {
            const eps = useEditPathStore()
            const ms = useMaps()

            if (index < - 1 || index >= eps.checkpoints.length) {
                return
            }

            // Get coordinates from the source checkpoint
            let sourceLng, sourceLat
            if (index === -1) {
                // Start point marker - use position property
                const startMarker = eps.markers[0]
                sourceLng = startMarker.position.lng
                sourceLat = startMarker.position.lat
            } else {
                const sourceCheckpoint = eps.checkpoints[index]
                sourceLng = sourceCheckpoint.features[0].geometry.coordinates[0]
                sourceLat = sourceCheckpoint.features[0].geometry.coordinates[1]
            }

            const newLng = sourceLng + 0.00003
            const newLat = sourceLat + 0.00003

            // Create new checkpoint object
            const newCheckpoint = {
                type: "FeatureCollection",
                features: [
                    {
                        id: (index + 1).toString(),
                        type: "Feature",
                        properties: {},
                        geometry: {
                            type: "Point",
                            coordinates: [newLng, newLat]
                        }
                    }
                ]
            }

            // Insert new checkpoint at index + 1
            eps.checkpoints.splice(index + 1, 0, newCheckpoint)

            // Copy the freeDrawingBooleans value from source checkpoint (default false for start point)
            const sourceFreeDrawingValue = index === -1 ? false : this.freeDrawingBooleans[index]
            const sourcePathIndiceForCPS = index === -1 ? 0 : this.pathIndicesForCPs[index + 1]
            this.freeDrawingBooleans.splice(index + 1, 0, sourceFreeDrawingValue)
            this.pathIndicesForCPs.splice(index + 1 + 1, 0, sourcePathIndiceForCPS)

            // Renumber all checkpoints
            eps.checkpoints.forEach((cp, i) => cp.features[0].id = i.toString())

            // Get all checkpoint markers
            const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))

            // Create visual marker for the new checkpoint
            const newNumber = index + 2 // +2 because checkpoints are 1-indexed
            let checkpointText = sourceFreeDrawingValue ? 'FP' : 'CP'
            checkpointText += newNumber

            const newMarker = eps.setPointMarker(
                { lat: newLat, lng: newLng },
                checkpointText,
                '#808080',
                `Checkpoint ${newNumber}`
            )

            // Add right-click context menu to new marker
            this.addContextMenuToMarker(newMarker)

            // Remove the new marker from its current position (end of array)
            eps.markers = eps.markers.filter(m => m !== newMarker)

            // Insert the marker in the correct position in the markers array
            // For index === -1, insert after the start point marker (markers[0])
            const insertPosition = index === -1
                ? 1
                : eps.markers.indexOf(checkpointMarkers[index]) + 1
            eps.markers.splice(insertPosition, 0, newMarker)

            // Update all checkpoint markers after insertion point - ONLY visual properties
            const updatedCheckpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
            updatedCheckpointMarkers.forEach((marker, i) => {
                const markerNumber = i + 1
                let str = this.freeDrawingBooleans[i] ? "FP" : "CP"
                const newText = `${str}${markerNumber}`
                marker.title = `Checkpoint ${markerNumber}`
                marker.markerId = `start_CP${markerNumber}`

                // Update visual text by recreating PinElement
                const newPinElement = new ms.google.maps.marker.PinElement({
                    background: '#FFA500',
                    borderColor: '#000000',
                    glyphColor: '#FFFFFF',
                    scale: 1.4,
                    glyphText: String(newText)
                })
                marker.content = newPinElement
            })

            this.updatedChanges = true
            this.fullPathGenerated = false
        },
        async switchCheckpoint(index) {
            const eps = useEditPathStore()
            const ms = useMaps()

            if (index < 0 || index >= eps.checkpoints.length) {
                return
            }

            // Toggle the freeDrawingBooleans value
            this.freeDrawingBooleans[index] = !this.freeDrawingBooleans[index]

            // Get the marker at this index
            const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
            const marker = checkpointMarkers[index]

            if (marker) {
                const markerNumber = index + 1
                const newStr = this.freeDrawingBooleans[index] ? 'FP' : 'CP'
                const newText = `${newStr}${markerNumber}`

                // Update marker properties
                marker.title = `Checkpoint ${markerNumber}`
                marker.markerId = this.freeDrawingBooleans[index] ? `start_FP${markerNumber}` : `start_CP${markerNumber}`

                // Update visual appearance immediately
                const newPinElement = new ms.google.maps.marker.PinElement({
                    background: '#FFA500',
                    borderColor: '#000000',
                    glyphColor: '#FFFFFF',
                    scale: 1.4,
                    glyphText: String(newText)
                })
                marker.content = newPinElement
            }
            await this.generateSegment(false, index)
        },
        pauseMapClickListener() {
            if (this.easyModeMapClickListener) {
                this.easyModeMapClickListener.remove()
                this.easyModeMapClickListener = null
                this._wasListeningBeforeMeasure = true
            }
        },
        resumeMapClickListener() {
            const eps = useEditPathStore()
            if (this._wasListeningBeforeMeasure && eps.startPoint && eps.endPoint) {
                this._wasListeningBeforeMeasure = false
                this.addMapClickListenerPoints()
            }
        },
        addMapClickListenerPoints() {
            const ms = useMaps()
            const eps = useEditPathStore()

            this.easyModeMapClickListener = ms.map.addListener('click', async (event) => {

                if (this.deleteButton) {
                    this.hideDeleteButton()
                    return
                }
                if (this.insertButton) {
                    this.hideInsertButton()
                    return
                }
                if (this.switchButton) {
                    this.hideSwitchButton()
                    return
                }
                if (this.highlightedPolylines.length > 0) {
                    this.clearPathHighlight()
                }

                const clickedPoint = {
                    lat: event.latLng.lat(),
                    lng: event.latLng.lng()
                }

                let pointToBeShown = clickedPoint
                // Snap to nearest path point if enabled
                if (!this.isFreeDrawingMode || eps.markersOnPath) {
                    const trackSystem = ms.draw.field.abLines.concat(ms.draw.field.headlanes) || 'none'
                    const nearestPoint = this.getClosestPointTurfEasy(clickedPoint, trackSystem)
                    pointToBeShown = nearestPoint || clickedPoint
                }
                // Push checkpoint in correct format
                eps.checkpoints.push({
                    type: "FeatureCollection",
                    features: [
                        {
                            id: eps.checkpoints.length.toString(),
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [pointToBeShown.lng, pointToBeShown.lat],
                            }
                        }
                    ]
                })

                if (this.isFreeDrawingMode) {
                    this.freeDrawingBooleans.push(true)
                } else {
                    this.freeDrawingBooleans.push(false)
                }

                // Create visual marker for the checkpoint
                let checkpointText = ''
                if (this.isFreeDrawingMode) {
                    checkpointText = 'FP' + eps.checkpoints.length;
                } else {
                    checkpointText = 'CP' + eps.checkpoints.length;
                }
                const checkpointTitle = 'Checkpoint ' + eps.checkpoints.length;

                const marker = eps.setPointMarker(
                    pointToBeShown,
                    checkpointText,
                    '#808080',
                    checkpointTitle
                )

                // Add right-click context menu for deletion
                this.addContextMenuToMarker(marker)

                this.updatedChanges = true
                this.fullPathGenerated = false
                this.generateSegment(false, eps.checkpoints.length - 1)
            })
        },
        // Generate path in easy mode with unlimited checkpoints
        async generateSegment(fullPath = false, highlightIndex = null) {
            const eps = useEditPathStore()
            const tasksStore = useTasksStore()
            const ps = usePlanner()

            if (!eps.startPoint || !eps.endPoint) {
                this.toastText = 'Start and end points are required'
                throw new Error('Start and end points are required')
            }

            if (!tasksStore.field || !tasksStore.field.id) {
                this.toastText = 'Field ID is not valid'
                throw new Error('Field ID is not valid')
            }

            // Format checkpoints according to the required structure
            const formattedCheckpoints = {
                type: "FeatureCollection",
                features: eps.checkpoints.map((checkpoint, index) => ({
                    id: index.toString(),
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: "Point",
                        coordinates: checkpoint.features[0].geometry.coordinates
                    }
                }))
            }

            // Prepare data in the exact format required
            const requestData = this.prepareRequestDataForSegment(
                eps.originalPathData,
                eps.startPoint,
                eps.endPoint,
                formattedCheckpoints,
                this.freeDrawingBooleans
            )
            let result = null
            try {
                if (this.isUpload && eps.markers.length === 2 && eps.markers[0].title === 'Start Point' && eps.markers[1].title === 'End Point' && fullPath) {
                    requestData.segment = this.uploadedSegment
                    result = await postUploadedPathPlanning(requestData)
                    this.isUpload = false
                } else {
                    result = fullPath ? await postEditedPathPlanning(requestData) : await postEditPathSegmentPlanning(requestData)
                    this.hasUploadedSegment = false
                }
            } catch (error) {
                console.error('Error during path generation:', error)
                this.toastText = error.response?.data?.message || 'Unknown error during path generation'
            }
            this.fullPathGenerated = fullPath

            const [warningIndices, receivedResult] = this.handlePathUpdateError(result, fullPath)

            if (receivedResult === null) {
                return {
                    success: false,
                    warnings: [],
                    warningIndices: []
                }
            }

            // Display the path on map
            await eps.displayEditedPath(receivedResult)

            // Show uncovered polygons if available
            if (fullPath) {
                ps.showUncoveredPolygons(result, false)
                if (ps.uncoveredZonesPulsing) {
                    ps.startUncoveredPulsing()
                }
                eps.uncoveredArea = result.uncovered_area
                if (result.path && result.path.features && result.path.features.length > 0) {
                    eps.pathCoordinatesToSave = result.path.features[0].geometry.coordinates
                }
            } else {
                this.downloadablePolys = eps.rainbowPolylines.length > 0 ? eps.rainbowPolylines : null
            }
            // Show warning markers
            this.warningIndices = warningIndices
            this.setDirectionWarning()
            this.updatedChanges = false

            // Extract warning_indices and pathIndicesForCPs from result
            this.pathIndicesForCPs = result.path_indices_for_cps || []
            this.clearPathHighlight() // Clear any existing highlights
            if (highlightIndex !== null) {
                this.highlightPathSegment(highlightIndex)
            }
            this.clearOrangeVertices() // Clear OrangeVertices, since we don't need them for the whole path
            return {
                success: true,
                warnings: result.warnings || [],
                warningIndices: warningIndices
            }
        },
        showMarkerError(location) {
            const eps = useEditPathStore()
            const ms = useMaps()
            const locationLng = location.coordinates[0]
            const locationLat = location.coordinates[1]

            // find the marker with the same location
            let markers = eps.markers.find(m => {
                const markerLat = m.position.lat
                const markerLng = m.position.lng

                // Compare with small tolerance for floating point precision
                const tolerance = 0.0001
                const latMatch = Math.abs(markerLat - locationLat) < tolerance
                const lngMatch = Math.abs(markerLng - locationLng) < tolerance

                if (latMatch && lngMatch) {
                    return latMatch && lngMatch
                }

            })

            if (markers) {
                markers = Array.isArray(markers) ? markers : [markers]
                markers.forEach(marker => {
                    // Extract checkpoint number from title
                    let markerLabel = ''
                    if (marker.title && marker.title.includes('Checkpoint')) {
                        const checkpointNum = marker.title.replace('Checkpoint ', '')
                        this.redPinIndex = parseInt(checkpointNum)
                        markerLabel = marker?.content?.glyphText || marker?.IftaLabel?.innerText || marker?.getLabel() || 'CP' + checkpointNum
                    }

                    if (markerLabel === '') {
                        // If it's not a checkpoint marker, use the label
                        markerLabel = marker?.content?.glyphText || marker?.IftaLabel?.innerText || marker?.getLabel() || ''
                    }

                    const redPinElement = new ms.google.maps.marker.PinElement({
                        background: '#FF0000',
                        borderColor: '#000000',
                        glyphColor: '#FFFFFF',
                        scale: 1.4,
                        glyphText: String(markerLabel)
                    })

                    marker.content = redPinElement

                    // Simple pulsing animation
                    this.redPinInterval = setInterval(() => {
                        const currentScale = redPinElement.style.transform
                        redPinElement.style.transform = currentScale === 'scale(1.8)' ? 'scale(1.4)' : 'scale(1.8)'
                    }, 500)
                })
            }
        },
        showWarningMarker(index) {
            const eps = useEditPathStore()
            let marker = null
            if (index === 0) {
                marker = eps.markers[0]
            } else if (index === eps.markers.length - 1) {
                marker = eps.markers[1]
            } else {
                const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
                marker = checkpointMarkers[index - 1]
            }

            if (marker) {
                const ms = useMaps()
                const markertext = marker?.content?.glyphText || marker?.IftaLabel?.innerText || marker?.getLabel() || 'CP' + index
                const yellowPinElement = new ms.google.maps.marker.PinElement({
                    background: '#FFFF00',
                    borderColor: '#555555',
                    glyphColor: '#000000',
                    scale: 1.4,
                    glyphText: String(markertext)
                })

                marker.content = yellowPinElement

                //Simple pulsing animation
                this.yellowWarningInterval = setInterval(() => {
                    yellowPinElement.style.transform = yellowPinElement.style.transform === 'scale(1.8)' ? 'scale(1.0)' : 'scale(1.8)'
                }, 500)
            }

        },
        stopRedPinAnimation() {
            if (this.redPinInterval) {
                clearInterval(this.redPinInterval)
                this.redPinInterval = null
                this.redPinIndex = null
                this.hideDeleteButton()
                this.deleteButton = null
                this.hideInsertButton()
                this.insertButton = null
                this.hideSwitchButton()
                this.switchButton = null
            }
            if (this.yellowWarningInterval) {
                clearInterval(this.yellowWarningInterval)
                this.yellowWarningInterval = null
                this.yellowPinElement = null
                this.hideDeleteButton()
                this.deleteButton = null
                this.hideInsertButton()
                this.insertButton = null
                this.hideSwitchButton()
                this.switchButton = null
            }
        },
        getClosestPointTurfEasy(coords, path, idx = -1) {
            const eps = useEditPathStore()
            const isfreeMarker = !eps.markersOnPath && this.isFreeDrawingMode
            const isStartOrEnd = idx === -1 || this.freeDrawingBooleans[idx]
            if (this.easyMode && isfreeMarker && eps.markers.length > 1 && !isStartOrEnd) {
                return null
            }
            const hasCoords = coords && coords.lng && coords.lat;
            const hasPath = path && path.length;

            if (!hasCoords || !hasPath) {
                console.warn('Invalid coordinates or path data')
                return null
            }

            const { lng, lat } = { lng: coords.lng, lat: coords.lat }
            const pointOnPath = getClosestPointPolyline({ lng, lat }, path)

            return pointOnPath
        },
        resetEasyModeState() {
            this.fullPathGenerated = false
            this.updatedChanges = false
            this.freeDrawingBooleans = []
            this.isFreeDrawingMode = false
            this.pathIndicesForCPs = []
            this.isUpload = false
            this.uploadedSegment = {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: null,
                            coordinates: null
                        },
                    },
                ],
            }
            this.warningIndices = []
            // Hide any open buttons
            this.hideDeleteButton()
            this.hideInsertButton()
            this.hideSwitchButton()
            this.stopRedPinAnimation()
            // Clear lines on the map
            this.clearOrangeVertices()
            this.clearPathHighlight()

            if (this._wasListeningBeforeMeasure !== null) {
                this._wasListeningBeforeMeasure = null
            }


            // Remove all checkpoint markers from map
            const eps = useEditPathStore()
            const checkpointMarkers = eps.markers.filter(m => m?.title?.includes('Checkpoint'))
            checkpointMarkers.forEach(marker => {
                try {
                    toRaw(marker).setMap(null)
                } catch (e) {
                    console.warn('Failed to remove marker:', e)
                }
            })
            this.cpDragListeners.forEach(listener => {
                this.removeDragListener(listener)
            })
            this.cpDragListeners = []
            if (this.easyModeMapClickListener) {
                this.easyModeMapClickListener.remove()
                this.easyModeMapClickListener = null
            }
            eps.pathCoordinatesToSave = null
        },
        handleKeyDown(event) {
            const isShiftA = event.shiftKey && event.code === 'KeyA'
            if (!isShiftA) return 

            if (isShiftA) {
                event.preventDefault()
                this.isFreeDrawingMode = !this.isFreeDrawingMode
            }
        },
        // Create orange vertices along the original path
        setOrangeVertices(pathData) {
            const ms = useMaps()

            if (!pathData || !pathData.features) {
                return
            }
            const geom = pathData?.features[0]?.geometry || pathData
            const pointsarray = geom.type === 'MultiLineString' ? geom.coordinates[0] : geom.coordinates
            const points = pointsarray.map((coord) => ({
                lat: coord[1],
                lng: coord[0]
            }))

            if (!points || points.length === 0) {
                console.warn('No points found in path')
                return
            }

            // Performance optimization: Only create markers for every Nth point to reduce DOM elements
            const MARKER_INTERVAL = Math.max(1, Math.floor(points.length / 50))

            // Create a marker for selected points in the path
            points.forEach((coord, index) => {
                // Skip points based on interval (always include first and last)
                if (index !== 0 && index !== points.length - 1 && index % MARKER_INTERVAL !== 0) {
                    return
                }

                const lat = coord.lat
                const lng = coord.lng

                try {
                    // Create a marker with perpendicular dash symbol
                    const marker = new ms.google.maps.marker.AdvancedMarkerElement({
                        position: { lat, lng },
                        map: toRaw(ms.map),
                        content: this.createDashIcon(0),
                        zIndex: 10, // Try to render below everything
                        collisionBehavior: ms.google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY
                    })
                    this.orangeVertices.push(marker)
                } catch (e) {
                    console.warn('Failed to create orange vertex marker:', e)
                }
            })
        },
        // Remove all orange vertex markers from map
        clearOrangeVertices() {
            this.orangeVertices.forEach(marker => {
                if (marker) {
                    marker.map = null
                }
            })
            // Clear the array
            this.orangeVertices = []
        },
        updateOrangeVertices(pathData) {
            const eps = useEditPathStore()
            if (!eps.startPoint || !eps.endPoint) {
                console.warn('Start and end points are required for updateOrangeVertices')
                return
            }
            this.clearOrangeVertices()
            this.setOrangeVertices(pathData)
        },
        createDashIcon(heading) {
            // Create a div with a dash that's perpendicular to the path
            const div = document.createElement('div')

            // The dash should be perpendicular, so add 90 degrees
            const perpendicularAngle = (heading + 90) % 360

            div.style.cssText = `
                width: 20px;
                height: 4px;
                background-color: #FFA500;
                transform: rotate(${perpendicularAngle}deg);
                transform-origin: center;
                border-radius: 1px;
                opacity: 1;
                pointer-events: none;
                position: relative;
            `

            return div
        },
        // Highlight path segment between neighboring checkpoints
        // Called with the index of the checkpoint to highlight around
        highlightPathSegment(checkpointIndex) {
            const eps = useEditPathStore()
            const ms = useMaps()
            const ps = usePlanner()

            // Clear previous highlight
            this.clearPathHighlight()

            // Check if we have pathIndicesForCPs
            if (!this.pathIndicesForCPs || this.pathIndicesForCPs.length === 0) {
                console.warn('No pathIndicesForCPs available for highlighting')
                return
            }

            // Get the rainbow polylines (the generated path)
            const pathPolylines = eps.rainbowPolylines.length > 0
                ? eps.rainbowPolylines
                : ps.rainbowPolylines

            if (!pathPolylines || pathPolylines.length === 0) {
                console.warn('No path polylines available for highlighting')
                return
            }

            // Calculate the indices range: from previous checkpoint to next checkpoint
            // For checkpoint at position N: highlight from pathIndicesForCPs[N-1] to pathIndicesForCPs[N+1]
            const startIdx = this.pathIndicesForCPs[checkpointIndex]
            const endIdx = checkpointIndex < this.pathIndicesForCPs.length - 2
                ? this.pathIndicesForCPs[checkpointIndex + 2]
                : this.pathIndicesForCPs[checkpointIndex + 1]

            // Extract coordinates for the segment to highlight
            const segmentCoordinates = []
            let currentIdx = 0

            // Go through all polylines and collect coordinates for the range
            pathPolylines.forEach((polyline, polylineIndex) => {
                const path = polyline.getPath()
                const pathLength = path.getLength()
                for (let i = 0; i < pathLength; i++) {
                    // Skip the first point of subsequent polylines to avoid duplicates
                    if (polylineIndex > 0 && i === 0) {
                        continue
                    }

                    if (currentIdx >= startIdx && currentIdx <= endIdx) {
                        const point = path.getAt(i)
                        segmentCoordinates.push({ lat: point.lat(), lng: point.lng() })
                    }
                    currentIdx++

                    if (currentIdx > endIdx) break
                }

                if (currentIdx > endIdx) return
            })

            // Create highlighted polyline (thicker)
            if (segmentCoordinates.length > 0) {
                const rawMap = toRaw(ms.map)
                const polyline = new ms.google.maps.Polyline({
                    path: segmentCoordinates,
                    strokeColor: '#00FF00', // Green color for highlight
                    strokeOpacity: 1.0,
                    strokeWeight: 16, // Thicker line
                    map: rawMap,
                    zIndex: 100, // 
                    clickable: false
                })

                // Store raw polyline to avoid Vue proxy issues
                this.highlightedPolylines.push(toRaw(polyline))
            }
        },
        // Clear path segment highlight
        clearPathHighlight() {
            const ms = useMaps()

            // Remove all highlighted polylines from map
            this.highlightedPolylines.forEach((polyline) => {
                try {
                    const rawPolyline = toRaw(polyline)
                    // Clear the path first
                    rawPolyline.setPath([])
                    // Remove all Google Maps event listeners
                    if (ms.google && ms.google.maps && ms.google.maps.event) {
                        ms.google.maps.event.clearInstanceListeners(rawPolyline)
                    }
                    // Finally remove from map
                    rawPolyline.setMap(null)

                    // Double-check: force remove by setting map explicitly
                    rawPolyline.map = null
                } catch (e) {
                    console.warn('Failed to remove highlighted polyline:', e)
                }
            })

            // Clear the array
            this.highlightedPolylines = []
        },
        prepareRequestDataForSegment(PathData, startPoint, endPoint, formatedCheckpoints, FreeDrawing) {
            const eps = useEditPathStore()
            const tS = useTasksStore()
            return {
                path: PathData,
                edit_start: {
                    type: "FeatureCollection",
                    features: [
                        {
                            id: "0",
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [startPoint.lng, startPoint.lat]
                            }
                        }
                    ]
                },
                edit_end: {
                    type: "FeatureCollection",
                    features: [
                        {
                            id: "0",
                            type: "Feature",
                            properties: {},
                            geometry: {
                                type: "Point",
                                coordinates: [endPoint.lng, endPoint.lat]
                            }
                        }
                    ]
                },
                checkpoints: formatedCheckpoints,
                checkpoints_type: FreeDrawing,
                turn_radius: eps.turnRadius,
                field_id: tS.field.id,
                task_type: eps.editedPathData.task_type,
                track_width: tS.field.track_width || DEFAULT_TRACK_WIDTH,
                corridor_threshold: this.corridorThreshold,
                direction_change_extension_distance: this.hookCurveExtension,
                path_type: eps.pathType,
            }
        },
        handlePathUpdateError(result, fullPath, saveNeighbourIndices = []) {
            //Check, if I got the result object, or the data
            let resultData = result?.data || result
            if (!resultData) {
                this.toastText = 'Unknown error during path generation'
                return [[], null]
            }
            // Handle path editing failures
            if (resultData.reason === 'path_editing_failure') {
                this.toastText = resultData.info
                throw new Error(resultData.info || 'Path editing failed')
            }
            if (resultData.reason === 'inaccurate_cut_points') {
                this.toastText = resultData.info
                throw new Error(resultData.info || 'Path editing failed')
            }
            // Handle localized input errors (can be multiple)
            if (resultData.reason === 'localized_input_error') {
                // Support both single location and multiple locations
                const locations = resultData.locations || (resultData.location ? [resultData.location] : [])

                if (locations.length > 0) {
                    // Mark all problematic checkpoints with red pin
                    locations.forEach((location) => {
                        this.showMarkerError(location)
                    })

                    // Throw generic error message
                    let errorMsg = ''
                    if (resultData.info && resultData.info.startsWith('Checkpoints have to be within')) {
                        errorMsg = resultData.info
                    } else {
                        errorMsg = locations.length === 1
                            ? 'This checkpoint is problematic to reach'
                            : 'These checkpoints are problematic to reach'
                    }

                    this.toastText = errorMsg
                    throw new Error(errorMsg)
                } else {
                    this.toastText = resultData.info || 'Unknown error during path generation'
                    throw new Error(resultData.info || 'Unknown error during path generation')
                }
            }
            let warningIndices = resultData.warning_indices || []
            if (warningIndices.length > 0 && saveNeighbourIndices.length > 0) {
                // Adjust indices to match overall checkpoints
                warningIndices = warningIndices.map(i => i + Math.max(saveNeighbourIndices[0], 0))
            }

            if (resultData.warnings && resultData.warnings.length > 0) {
                this.toastText = resultData.warnings
            }
            if (resultData.problem) {
                this.toastText = resultData.problem
                console.error('Path generation problem:', resultData.problem)
            }
            const receivedResult = fullPath ? resultData.path : resultData.segment
            return [warningIndices, receivedResult]
        },
        addStartContextMenu(startMarker) {
            if (!startMarker) return

            const rawMarker = toRaw(startMarker)
            if (rawMarker.hasContextMenu) return // Avoid adding multiple listeners
            rawMarker.hasContextMenu = true
            const eps = useEditPathStore()

            //Clear other options manually
            this.hideDeleteButton()
            this.hideSwitchButton()

            // Add right-click handler for context menu
            rawMarker.addEventListener('contextmenu', (event) => {
                event.preventDefault()
                if (eps.markers.length >= 2) {
                    this.showInsertButton(event, -1)
                }
            })
        },
        setDirectionWarning() {
            if (this.warningIndices.length > 0) {
                try {
                    // toastText is being watched
                    this.toastText = `Wrong driving direction over AB-line detected at checkpoints: `
                    this.warningIndices.forEach(i => this.showWarningMarker(i))
                } catch (e) {
                    console.error('Failed to show warning markers:', e)
                }
            }
        },
        async downloadPath() {
            if (!this.downloadablePolys || this.downloadablePolys.length === 0) {
                this.toastText = 'No path segment available for download'
                return
            }
            // Convert Google Maps coordinates to GeoJSON LineString format [lng, lat]
            // Extract coordinates from all rainbow polylines
            const lines = []

            this.downloadablePolys.forEach((polyline) => {
                const rawPolyline = toRaw(polyline)
                const path = rawPolyline.getPath()
                const lineCoords = []
                for (let i = 0; i < path.getLength(); i++) {
                    const point = path.getAt(i)
                    lineCoords.push([point.lng(), point.lat()])
                }
                lines.push(lineCoords)
            })
            const feature = {
                type: 'Feature',
                geometry: {
                    type: 'MultiLineString',
                    coordinates: lines
                },
                properties: {
                    name: 'Path Segment'
                }
            }

            const fileName = `path_segment_${moment().format('YYYY-MM-DD-HH-mm')}`
            await downloadFeatureAsShapefile(feature, fileName)
        },
        async uploadPathFile(file) {
            const eps = useEditPathStore()
            const ms = useMaps()
            const ps = usePlanner()
            const ts = useTasksStore()
            const formData = new FormData()
            formData.append('file', file)
            formData.append('desired_type', 'multi_line') // Specify the desired geometry type
            const response = await postExtractGeometryFromShape(formData)
            if (!response) {
                return 'error'
            }
            let coordinates = null
            if (response.multi_line) {
                coordinates = response.multi_line.coordinates
            } else if (response.line) {
                coordinates = [response.line.coordinates]
            } else {
                console.warn('No valid geometry found in response')
                return 'no_line'
            }

            // Clear existing edited path
            await this.resetForNewEdit(false)
            ps.hideOldPath()
            ps.clearUpdatePathPolylines()
            eps.showOriginalPath = false
            this.uploadedSegment.features[0].geometry.type = "MultiLineString"
            this.uploadedSegment.features[0].geometry.coordinates = coordinates

            // MultiLineString: array of line arrays, each with [lng, lat] pairs
            const lines = coordinates
            this.setStartAndEndFromUpload(lines)
            eps.updateOrangePath()

            // const field_poly = toRaw(ms.draw.field?.outerborder.coordinates) || null
            const field = ms.fields.find(f => f.id === toRaw(ts.field.id))
            const fieldGeometry = toRaw(field?.outer_border) || null

            if (
                fieldGeometry?.type === 'Polygon' &&
                Array.isArray(fieldGeometry.coordinates) &&
                Array.isArray(lines)
            ) {
                const fieldFeature = feature(fieldGeometry, {});
                if (lines && Array.isArray(lines)) {
                    lines.forEach(line => {
                        const lineFeature = feature({
                            type: 'LineString',
                            coordinates: line
                        }, {})
                        if (!booleanContains(fieldFeature, lineFeature)) {
                            this.toastText =
                                'Warning: Uploaded path is not contained inside the field boundaries. Please check the path and adjust if necessary.';
                        }
                    })
                }
            }

            lines.forEach((lineCoords, idx) => {
                const path = lineCoords.map(([lng, lat]) =>
                    new ms.google.maps.LatLng(lat, lng)
                )

                const polyline = new ms.google.maps.Polyline({
                    path,
                    strokeColor: eps.rainbowColors[idx % eps.rainbowColors.length],
                    strokeOpacity: 1.0,
                    strokeWeight: 5,
                    map: toRaw(ms.map),
                    clickable: false,
                    zIndex: 1001,
                })

                eps.rainbowPolylines.push(toRaw(polyline))
            })


            this.fullPathGenerated = false
            this.updatedChanges = true
            this.isUpload = true
            this.hasUploadedSegment = true
            return 'success'
        },
        async resetForNewEdit(showOriginalPath = true) {
            const eps = useEditPathStore()
            const ms = useMaps()
            const ps = usePlanner()
            const ts = useTasksStore()
            // Reset the state to start a new edit
            this.resetEasyModeState()
            eps.showOriginalPath = true
            this.stopRedPinAnimation()
            eps.clearAllElements()
            this.downloadablePolys = []
            // Clear the GeoJSON data representing the orange lines
            ms.map.data.forEach(feature => {
                if (feature.getProperty('type') === 'editing_segment') {
                    ms.map.data.remove(feature)
                }
            })

            eps.clearEditingState()

            // Re-initialize the editing
            eps.removeMapClickListener()
            eps.addMapClickListener()

            if (showOriginalPath) {
                ps.drawPath(eps.originalPathData)
                eps.uncoveredArea = await eps.showUncoveredArea(ts.field.id)
            }
            eps.pathCoordinatesToSave = null
        },
        setStartAndEndFromUpload(lines) {
            const eps = useEditPathStore()
            const firstLine = lines[0] || []
            const lastLine = lines[lines.length - 1] || []
            const firstCoord = firstLine[0]
            const lastCoord = lastLine[lastLine.length - 1]
            if (!firstCoord || !lastCoord) {
                return
            }
            const [startLng, startLat] = firstCoord
            const [endLng, endLat] = lastCoord
            const startLatLng = { lat: startLat, lng: startLng }
            const endLatLng = { lat: endLat, lng: endLng }

            eps.startPoint = startLatLng
            eps.endPoint = endLatLng
            const startMarker = eps.setPointMarker(startLatLng, 'S', '#00FF00', 'Start Point')
            this.addStartContextMenu(startMarker)


            // Place end marker — reuses setPointMarker which sets eps.endPoint
            eps.setPointMarker(endLatLng, 'E', '#FF0000', 'End Point')
            // Restore the correct listeners
            eps.removeMapClickListener()
            this.addMapClickListenerPoints()
            eps.setPathOrange()


        },
        async getDefaultEditValues() {
            const result = await getDefaultEditValues()
            if (!result || result.problem) {
                this.toastText = 'Connection error: Failed to fetch default edit values'
                return
            }
            this.corridorThreshold = result?.corridor_threshold ?? this.corridorThreshold
            this.hookCurveExtension = result?.direction_change_extension_distance ?? this.hookCurveExtension
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useEasyModeStore, import.meta.hot))
}
